import { InfoIcon, PlusIcon } from '@phosphor-icons/react';
import { Button } from 'components/button/Button';
import { FormikProvider } from 'formik';
import { useFormik } from 'components/form/formik';
import { updateOrderLinePurchase } from '../../models';
import diff from 'object-diff';
import { useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { invariant } from '@utils/invariant';
import * as Yup from 'yup';

import { ISimpleComboboxItem } from '../../../../components/combobox/SimpleCombobox';
import { InputField } from '../../../../components/input/InputField';
import {
  calculateDieselSurchargeFromLine,
  findDieselSurchargeBaseLine,
  getEffectiveDieselSurchargePercentage,
  isDieselSurchargeProductType,
} from '../../../../domain/diesel-surcharge';
import { getDisplayError } from '../../../../utils/get-display-error';
import { decimateAndFormatNumber, parseNumberInput } from '../../../../utils/number';
import { ProductTypeComboboxField, productTypeToComboboxItem } from '../../productType/ProductTypeComboboxField';
import { useOrder } from '../order/orderContext';
import { useOrderLine } from '../orderLine/orderLineContext';
import { OrderLinePurchase } from './OrderPurchases';
import { VatRateComboboxField, vatRateToComboboxItem } from '../../vatRate/VatRateComboboxField';
import { useAuth } from '../../../../contexts/auth-context';
import { roleToNumber } from '../../../auth/roles.client';
import { Alert } from '../../../../components/alert/Alert';
import { captureException } from '@sentry/react';
import { useMinimalGeneralSettings } from '../../../../contexts/minimal-settings-context';

const createOrderPurchaseSchema = Yup.object().shape({
  productType: Yup.mixed().nullable().required('Vereist'),
  vatRate: Yup.mixed().nullable().required('Vereist'),
  amount: Yup.number().required('Vereist'),
  unitPrice: Yup.number().required('Vereist'),
  externalNote: Yup.string().nullable(),
});

interface IOrderLinePurchaseValues {
  productType: ISimpleComboboxItem | null;
  vatRate: ISimpleComboboxItem | null;
  amount: string;
  unitPrice: string;
  externalNote: string;
}

interface IPageContentProps {
  orderPurchase: OrderLinePurchase;
}

const PageContent: React.FC<IPageContentProps> = (props) => {
  const { orderPurchase } = props;

  const navigate = useNavigate();
  const { order } = useOrder();
  const { orderLine, refreshData } = useOrderLine();
  const { settings } = useMinimalGeneralSettings();

  const initialValues: IOrderLinePurchaseValues = useMemo(() => {
    return {
      productType: productTypeToComboboxItem(orderPurchase.productType),
      vatRate: vatRateToComboboxItem(orderPurchase.vatRate),
      amount: decimateAndFormatNumber(orderPurchase.amount, 2),
      unitPrice: decimateAndFormatNumber(orderPurchase.unitPrice, 2),
      externalNote: orderPurchase.externalNote ?? '',
    };
  }, [orderPurchase]);

  const formikbag = useFormik({
    initialValues,
    validationSchema: createOrderPurchaseSchema,
    onSubmit: async (values) => {
      try {
        const patch: Partial<typeof values> = diff(initialValues, values);
        const { productType, vatRate, amount, unitPrice, ...otherValues } = patch;
        await updateOrderLinePurchase(orderPurchase.id, {
          productTypeId: productType != null ? productType.key : undefined,
          vatRateId: vatRate != null ? vatRate.key : undefined,
          amount: amount != null ? parseNumberInput(amount, 2) : undefined,
          unitPrice: unitPrice != null ? parseNumberInput(unitPrice, 2) : undefined,
          ...otherValues,
        });
        await refreshData();
        navigate('..');
        toast.success('Aankoop aangepast');
      } catch (err: any) {
        captureException(err);
        toast.error('Kon aankoop niet aanpassen: ' + getDisplayError(err));
      }
    },
  });
  const { handleSubmit, isSubmitting, values, setFieldValue } = formikbag;

  const dieselSurchargePercentage = getEffectiveDieselSurchargePercentage(
    order.supplier,
    settings.dieselSurchargePercentage,
  );
  const isDieselSurchargeSelected = isDieselSurchargeProductType(
    values.productType?.key,
    settings.dieselSurchargeProductType?.id,
  );

  useEffect(() => {
    if (!isDieselSurchargeSelected || values.productType?.key === orderPurchase.productType.id) {
      return;
    }
    const basePurchase = findDieselSurchargeBaseLine(
      orderLine.purchases
        .filter((purchase) => purchase.id !== orderPurchase.id)
        .map((purchase) => ({
          productTypeId: purchase.productType.id,
          amount: purchase.amount,
          unitPrice: purchase.unitPrice,
        })),
      {
        baseProductTypeId: settings.dieselSurchargeBaseProductType?.id,
        defaultProductTypeId: settings.defaultProductType?.id,
        dieselProductTypeId: settings.dieselSurchargeProductType?.id,
      },
    );
    if (!basePurchase) {
      return;
    }
    const surchargeValue = calculateDieselSurchargeFromLine(basePurchase, dieselSurchargePercentage);
    setFieldValue('amount', '1');
    setFieldValue('unitPrice', decimateAndFormatNumber(surchargeValue, 2));
  }, [
    dieselSurchargePercentage,
    isDieselSurchargeSelected,
    orderLine.purchases,
    orderPurchase.id,
    orderPurchase.productType.id,
    setFieldValue,
    settings.defaultProductType?.id,
    settings.dieselSurchargeBaseProductType?.id,
    settings.dieselSurchargeProductType?.id,
    values.productType?.key,
  ]);

  return (
    <>
      <h1 className="heading-one mb-4">Pas aankoop aan</h1>

      <FormikProvider value={formikbag}>
        <form onSubmit={handleSubmit}>
          <ProductTypeComboboxField labelText="Product" name="productType" />

          {isDieselSurchargeSelected && (
            <div className="mb-4 text-sm">
              {`Diesel toeslag voor vervoerder is ${decimateAndFormatNumber(dieselSurchargePercentage, 2)}%`}
            </div>
          )}

          <div className="flex gap-2">
            <div className="flex-2">
              <InputField labelText="Aantal" type="number" name="amount" step="0.01" />
            </div>
            <div className="flex-2">
              <InputField labelText="Eenheidsprijs" type="number" name="unitPrice" step="0.01" />
            </div>
            <div className="flex-3">
              <VatRateComboboxField labelText="Btw Tarief" name="vatRate" />
            </div>
          </div>

          <InputField labelText="Extra info" type="text" name="externalNote" spellCheck={true} />

          <Button
            type="submit"
            color="primary"
            iconLeft={<PlusIcon className="button-icon" />}
            isDisabled={isSubmitting}
            isLoading={isSubmitting}
          >
            Pas aankoop aan
          </Button>
        </form>
      </FormikProvider>
    </>
  );
};

export const UpdateOrderPurchasePage = () => {
  const { order } = useOrder();
  const { orderLine } = useOrderLine();
  const { orderPurchaseId: _orderPurchaseId } = useParams<{ orderPurchaseId: string }>();
  invariant(_orderPurchaseId);
  const orderPurchaseId = +_orderPurchaseId;
  const { me } = useAuth();
  const isPurchaseReadOnly = order.purchaseLocked && roleToNumber(me.role) < roleToNumber('InternalAdmin');

  const orderPurchase = useMemo(() => {
    return orderLine.purchases.find((c) => c.id === orderPurchaseId);
  }, [orderLine, orderPurchaseId]);

  if (isPurchaseReadOnly) {
    return (
      <Alert type="info" icon={<InfoIcon className="w-5 h-5" />} title="Aankoopzijde geblokkeerd">
        Het ritoverzicht of de self billing van deze order is al verzonden, dus aankopen kunnen niet meer worden
        aangepast.
      </Alert>
    );
  }

  if (!orderPurchase) {
    return <div>Inhoud niet gevonden</div>;
  }

  return <PageContent orderPurchase={orderPurchase} />;
};
